import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';

export default function Problem() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  
  // Parallax effects
  const parallax1 = useParallax({ speed: 0.35, direction: 'up' });
  const parallax2 = useParallax({ speed: 0.45, direction: 'down' });
  const parallax3 = useParallax({ speed: 0.25, direction: 'up' });

  const scrollToContact = () => {
    const element = document.getElementById('contato');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const problems = [
    {
      icon: 'ri-edit-line',
      title: 'Marcações feitas por planilhas e cadernos',
      description: 'Registros imprecisos que não refletem a realidade do trabalho',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/5f463ad8b16c69d7a92a721fdb218f68.jpeg',
      color: 'from-red-500 to-rose-600',
      bgColor: 'bg-red-50',
      iconBg: 'bg-red-500',
      iconHoverBg: 'group-hover:bg-red-600',
      textHover: 'group-hover:text-red-600'
    },
    {
      icon: 'ri-time-line',
      title: 'Falta de rastreamento e precisão nos registros de presença',
      description: 'Impossibilidade de saber quem está onde e quando',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/0d9c6fb7fea9a7514f9f7179200fbc34.png',
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50',
      iconBg: 'bg-orange-500',
      iconHoverBg: 'group-hover:bg-orange-600',
      textHover: 'group-hover:text-orange-600'
    },
    {
      icon: 'ri-tools-line',
      title: 'Equipamentos que não suportam ambientes rurais',
      description: 'Tecnologia urbana que falha nas condições do campo',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/abfae809bfb6dab90a76da431f84719d.png',
      color: 'from-yellow-500 to-amber-500',
      bgColor: 'bg-yellow-50',
      iconBg: 'bg-yellow-500',
      iconHoverBg: 'group-hover:bg-yellow-600',
      textHover: 'group-hover:text-yellow-600'
    },
    {
      icon: 'ri-question-line',
      title: 'Horas extras que ninguém sabe de onde surgiram',
      description: 'Custos inesperados sem justificativa clara',
      image: 'https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/d62ec8bf7afd9cf08cc82f074e638c67.png',
      color: 'from-rose-500 to-pink-600',
      bgColor: 'bg-rose-50',
      iconBg: 'bg-rose-500',
      iconHoverBg: 'group-hover:bg-rose-600',
      textHover: 'group-hover:text-rose-600'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % problems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + problems.length) % problems.length);
  };

  return (
    <section 
      ref={ref}
      id="problema" 
      className={`py-20 lg:py-28 bg-gradient-to-b from-white via-red-50/30 to-orange-50/30 relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      {/* Elementos decorativos coloridos com parallax */}
      <div className="absolute inset-0 opacity-20">
        <div 
          className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-red-400 to-rose-500 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${parallax1}px)` }}
        ></div>
        <div 
          className="absolute bottom-20 left-10 w-72 h-72 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '1s', transform: `translateY(${parallax2}px)` }}
        ></div>
        <div 
          className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '2s', transform: `translateY(${parallax3}px)` }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
              <i className="ri-alert-line mr-2"></i>
              Problemas Reais
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            O que acontece hoje no campo
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            No agro, o trabalho não começa nem termina na sede. Equipes estão espalhadas, o sinal falha, o calor castiga e o controle de ponto vira um problema diário.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          <div className="space-y-6">
            {problems.map((problem, index) => (
              <div
                key={index}
                className={`group flex items-start space-x-4 p-6 rounded-2xl transition-all duration-300 cursor-pointer border-2 ${
                  currentSlide === index
                    ? `${problem.bgColor} shadow-2xl scale-105 border-transparent`
                    : 'bg-white hover:bg-gray-50 hover:shadow-xl border-gray-100'
                }`}
                onClick={() => setCurrentSlide(index)}
              >
                <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg ${
                  currentSlide === index ? `${problem.iconBg} scale-110 rotate-3` : `bg-gray-100 ${problem.iconHoverBg}`
                }`}>
                  <i className={`${problem.icon} text-2xl transition-colors ${
                    currentSlide === index ? 'text-white' : 'text-gray-600 group-hover:text-white'
                  }`}></i>
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-bold text-gray-900 mb-2 transition-colors ${
                    currentSlide === index ? '' : problem.textHover
                  }`}>
                    {problem.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{problem.description}</p>
                </div>
                {currentSlide === index && (
                  <div className={`absolute right-6 top-6 w-3 h-3 rounded-full bg-gradient-to-r ${problem.color} animate-pulse`}></div>
                )}
              </div>
            ))}
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] border-4 border-white">
              {problems.map((problem, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-500 ${
                    currentSlide === index
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-95 pointer-events-none'
                  }`}
                >
                  <img
                    src={problem.image}
                    alt={problem.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${problem.color} opacity-20`}></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  <div className="absolute top-0 left-0 right-0 p-4 text-white md:hidden">
                    <div className={`inline-block px-4 py-1 rounded-full bg-gradient-to-r ${problem.color} text-white text-xs font-bold shadow-lg`}>
                      Problema {index + 1}
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className={`hidden md:inline-block px-4 py-1 rounded-full bg-gradient-to-r ${problem.color} text-white text-xs font-bold mb-3 shadow-lg`}>
                      Problema {index + 1}
                    </div>
                    <h4 className="text-xl font-bold mb-2">{problem.title}</h4>
                    <p className="text-sm text-white/90">{problem.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl hover:bg-white transition-all hover:scale-110 border-2 border-gray-100"
            >
              <i className="ri-arrow-left-s-line text-2xl text-gray-900"></i>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl hover:bg-white transition-all hover:scale-110 border-2 border-gray-100"
            >
              <i className="ri-arrow-right-s-line text-2xl text-gray-900"></i>
            </button>

            <div className="flex justify-center gap-2 mt-6">
              {problems.map((problem, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    currentSlide === index ? `w-10 bg-gradient-to-r ${problem.color} shadow-lg` : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-center px-4">
          <button
            onClick={scrollToContact}
            className="group bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 text-white px-6 sm:px-10 py-4 rounded-xl text-base sm:text-lg font-bold hover:from-red-700 hover:via-orange-700 hover:to-amber-700 transition-all duration-300 whitespace-nowrap transform hover:scale-105 hover:shadow-2xl border-2 border-white/20 w-full sm:w-auto max-w-md"
          >
            <span className="flex items-center justify-center gap-2">
              Converse com um especialista
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}